/* eslint-disable max-lines */
import React, { useEffect } from 'react';
import ProductListing from './ProductListing';
import OtherLooksInfusions from './OtherLooksInfusions';
import LookInteractiveImage from './LookInteractiveImage';
import {
    PageWrapper,
    MainBannerDiv,
    MainBannerImageSection,
    LooksTextContainer,
    StyledTitle,
    StyledDescription,
} from './LooksDetailPageElements';
import { ConnectedLooks } from './ConnectedLooks';
import useBellaTranslator from 'Client/hooks/useTranslator';
import { fetchLookDetailIfNeeded } from 'Client/redux/lookDetail/actions';
import useFetchingAction from 'Client/hooks/useFetchingAction/useFetchingAction';
import StatusCodeComponentWrapper from 'Client/components/StaticContent/StatusCodeComponentWrapper';
import NotFound from 'Client/components/NotFound';
import useConfig from 'Client/hooks/useConfig';
import { useLooksTracking } from 'Client/components/Looks/hooks/useLooksTracking';
import usePrevious from 'Client/hooks/usePrevious';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import { useBellaSelector } from 'Client/redux/hooks';
import useBellaPush from 'Client/hooks/useBellaHistoryPush';
import { TrackLooksActions } from 'Common/ga/types';

interface Props {
    lookSlug: string;
}

const LooksDetailPage = ({ lookSlug }: Props) => {
    const { countryCode, currency } = useConfig();
    useFetchingAction(fetchLookDetailIfNeeded(lookSlug, { countryCode, currency }));
    const t = useBellaTranslator();
    const bellaPush = useBellaPush();
    const { LDP } = useLooksTracking();
    const { p100IncreasedDesktopPageWidth } = useFeatureToggle();

    const { isFetching, error: looksError, look } = useBellaSelector(state => state.lookDetailPage);
    const { products } = look;

    const previousLook = usePrevious(look.slug);
    const isPortrait = !look.isLandscape;

    useEffect(() => {
        const isDataFetched = look.slug !== '' && previousLook !== look.slug && !isFetching;
        if (isDataFetched) {
            LDP.trackPageView(look);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetching]);

    const handleHotSpotClick = (productSku: string) =>
        LDP.track(TrackLooksActions.LDPHotSpotClick, `Look: ${look.trackingName}, Product: ${productSku}`);

    const handleWishlistBadgeClick = (newIsSelectedValue: boolean) =>
        LDP.track(
            newIsSelectedValue ? TrackLooksActions.LDPAddLookWishlist : TrackLooksActions.LDPRemoveLookWishlist,
            look.trackingName
        );

    const handleImageClick = () => LDP.track(TrackLooksActions.LDPImageClick, look.trackingName);

    const handleProductOverlayClick = (productSku: string) =>
        LDP.track(TrackLooksActions.LDPProductOverlayClick, `Look: ${look.trackingName}, Product: ${productSku}`);
    const handleCartButtonClick = () => LDP.track(TrackLooksActions.LDPCartButtonClick, look.trackingName);

    const hasError = looksError && !isFetching;
    if (hasError) {
        if (looksError.code === 404) {
            bellaPush('/looks/');
            return null;
        }
        return (
            <StatusCodeComponentWrapper code={looksError.code || 404}>
                <NotFound />
            </StatusCodeComponentWrapper>
        );
    }

    return (
        <PageWrapper data-testid="looks-detail-page" p100IncreasedDesktopPageWidth={p100IncreasedDesktopPageWidth}>
            <ConnectedLooks p100IncreasedDesktopPageWidth={p100IncreasedDesktopPageWidth} />
            <MainBannerDiv isPortrait={isPortrait} p100IncreasedDesktopPageWidth={p100IncreasedDesktopPageWidth}>
                <MainBannerImageSection
                    isPortrait={isPortrait}
                    p100IncreasedDesktopPageWidth={p100IncreasedDesktopPageWidth}
                >
                    <LookInteractiveImage
                        onHotSpotClick={handleHotSpotClick}
                        onWishlistBadgeClick={handleWishlistBadgeClick}
                        onImageClick={handleImageClick}
                        onProductOverlayClick={handleProductOverlayClick}
                        onCartButtonClick={handleCartButtonClick}
                        look={look}
                        isFetching={isFetching}
                    />
                </MainBannerImageSection>
                <LooksTextContainer isPortrait={isPortrait}>
                    <StyledTitle
                        isPortrait={isPortrait}
                        hasLongWord={look.name.split(' ').some(word => word.length > 10)}
                    >
                        {look.name}
                    </StyledTitle>
                    <StyledDescription
                        isPortrait={isPortrait}
                        text={look.caption}
                        readMoreText={`(${t('read more')})`}
                        lineClamp={7}
                        p100IncreasedDesktopPageWidth={p100IncreasedDesktopPageWidth}
                    />
                </LooksTextContainer>
            </MainBannerDiv>
            <ProductListing products={products} lookTrackingName={look.trackingName} />
            <OtherLooksInfusions lookSlug={lookSlug} />
        </PageWrapper>
    );
};

export default LooksDetailPage;
